import React from 'react';

const BASE =
  'w-full bg-white border rounded-md h-10 px-3 text-[13px] outline-none transition focus:border-teal-600 focus:ring-2 focus:ring-teal-50 placeholder:text-slate-400';

const STATE = {
  default: 'border-slate-200',
  error:   'border-rose-400 ring-2 ring-rose-100',
  success: 'border-emerald-400 ring-2 ring-emerald-50',
};

export function Label({ children, required, htmlFor }) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-1.5 block"
    >
      {children}
      {required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
  );
}

export function HelperText({ error, success, children }) {
  if (error)   return <p className="text-[11.5px] text-rose-600 mt-1.5">{children}</p>;
  if (success) return <p className="text-[11.5px] text-emerald-600 mt-1.5">{children}</p>;
  return <p className="text-[11.5px] text-slate-400 mt-1.5">{children}</p>;
}

export function Input({
  state = 'default',
  leadingIcon: LeadingIcon,
  trailingIcon: TrailingIcon,
  className = '',
  ...props
}) {
  const hasLeading  = Boolean(LeadingIcon);
  const hasTrailing = Boolean(TrailingIcon);

  if (!hasLeading && !hasTrailing) {
    return (
      <input
        className={`${BASE} ${STATE[state] || STATE.default} ${className}`}
        {...props}
      />
    );
  }

  return (
    <div className="relative">
      {hasLeading && (
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <LeadingIcon size={14} strokeWidth={1.75} />
        </span>
      )}
      <input
        className={`${BASE} ${STATE[state] || STATE.default} ${hasLeading ? 'pl-8' : ''} ${hasTrailing ? 'pr-8' : ''} ${className}`}
        {...props}
      />
      {hasTrailing && (
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          <TrailingIcon size={14} strokeWidth={1.75} />
        </span>
      )}
    </div>
  );
}

export function Select({ state = 'default', className = '', children, ...props }) {
  return (
    <select
      className={`${BASE} ${STATE[state] || STATE.default} cursor-pointer ${className}`}
      {...props}
    >
      {children}
    </select>
  );
}

export function Textarea({ state = 'default', className = '', rows = 3, ...props }) {
  return (
    <textarea
      rows={rows}
      className={`w-full bg-white border rounded-md px-3 py-2.5 text-[13px] outline-none transition focus:border-teal-600 resize-none placeholder:text-slate-400 ${STATE[state] || STATE.default} ${className}`}
      {...props}
    />
  );
}

export function FormField({ label, required, helper, error, success, children }) {
  return (
    <div>
      {label && <Label required={required}>{label}</Label>}
      {children}
      {helper && <HelperText error={!!error} success={!!success}>{error || success || helper}</HelperText>}
    </div>
  );
}

export function ToggleGroup({ options = [], value, onChange }) {
  return (
    <div className="flex bg-white border border-slate-200 rounded-md p-0.5 text-[11px]">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-2.5 py-1 rounded font-semibold transition ${
            value === opt.value
              ? 'bg-primary text-white'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default Input;
